import { useEffect, useState } from "react";

import { Button, CountryCodeMapping, PageContainer, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useParams, useSearchParams } from "react-router-dom";

import { BillingDetailsUpdateModal } from "../components/BillingDetailsUpdateModal/BillingDetailsUpdateModal";
import CTARow from "../components/CTARow/CTARow";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import PaymentUpdateModal from "../components/PaymentUpdateModal/PaymentUpdateModal";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import useNavigation from "../hooks/navigation";
import useAccount from "../hooks/useAccount";
import { useBilling } from "../hooks/useBilling";
import { useTracker } from "../hooks/useTracker";
import useTranslation from "../hooks/useTranslation";
import { ChargebeePaymentType, PaymentTypes } from "../lib/ChargebeeAdapter";
import { ElementClickAction } from "../lib/TrackingAdapter";
import { SearchParam } from "../types";
import { getStateFromStateCode, getVATNumberPrefix, isCountryInEU } from "../utils/countryUtils";

import style from "./TeamBillingPage.module.css";

const TeamBillingPage = () => {
  const { t, m } = useTranslation();
  const { teamID: teamIDFromParams } = useParams();
  const navigation = useNavigation();
  const { data: accountData, isLoading: isAccountLoading } = useAccount();
  useEffect(() => {
    if (accountData && !accountData.hasChargebeeSubscription) {
      navigation.goToProfile();
    }
  }, [accountData, navigation]);

  const teamID = teamIDFromParams ?? accountData?.accountTeamID;
  const tracker = useTracker();

  const [searchParams] = useSearchParams();
  const [isUpdateBillingDetailsModalOpen, setIsUpdateBillingDetailsModalOpen] = useState(false);
  const [isUpdatePaymentModalOpen, setIsUpdatePaymentModalOpen] = useState(
    searchParams.get(SearchParam.EditPaymentModalOpen) === "true"
  );

  const { data: billingData, isLoading: isBillingLoading } = useBilling(teamID);

  const paymentSource = billingData?.primaryPaymentSource;

  if (
    isBillingLoading ||
    isAccountLoading ||
    (accountData && !accountData.hasChargebeeSubscription) ||
    !teamID
  ) {
    return <SpinnerCenter />;
  }

  return (
    <UnderMaintenanceOrChildren>
      <PageContainer title={t(m.TeamBilling.Title)}>
        <CTARow
          title={t(m.TeamBilling.BillingDetails)}
          text={
            <div className={style.billingList}>
              {billingData?.billingDetails?.company && (
                <p>{billingData?.billingDetails?.company}</p>
              )}
              {billingData?.billingDetails?.line1 && <p>{billingData?.billingDetails?.line1}</p>}
              {billingData?.billingDetails?.line2 && <p>{billingData?.billingDetails?.line2}</p>}
              {billingData?.billingDetails?.city && <p>{billingData?.billingDetails?.city}</p>}
              {billingData?.billingDetails?.stateCode && (
                <p>
                  {getStateFromStateCode(
                    billingData.billingDetails.countryCode,
                    billingData.billingDetails.stateCode
                  )}
                </p>
              )}
              {billingData?.billingDetails?.zip && <p>{billingData?.billingDetails?.zip}</p>}
              {billingData?.billingDetails?.countryCode && (
                <p>
                  {CountryCodeMapping.find(
                    (country) => country.code === billingData?.billingDetails?.countryCode
                  )?.name ?? billingData?.billingDetails?.countryCode}
                </p>
              )}
              {billingData?.billingDetails?.vatNumber && (
                <p className={style.VATNumber}>
                  <span>
                    {isCountryInEU(billingData?.billingDetails?.countryCode)
                      ? `${t(m.TeamBilling.VatNumber)} `
                      : `${t(m.TeamBilling.TaxId)} `}
                  </span>
                  <span>
                    {`${getVATNumberPrefix(billingData.billingDetails.countryCode)}${
                      billingData?.billingDetails?.vatNumber
                    }`}
                  </span>
                </p>
              )}
              {(!billingData?.billingDetails ||
                !Object.values(billingData?.billingDetails).some(Boolean)) && (
                <p>{t(m.TeamBilling.NoBillingDetails)}</p>
              )}
            </div>
          }
          CTA={
            <Button
              variant="borderless"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.EDIT_BILLING_DETAILS,
                });
                setIsUpdateBillingDetailsModalOpen(true);
              }}
            >
              {t(m.TeamBilling.Edit)}
            </Button>
          }
        />
        <CTARowDivider />
        <CTARow
          title={t(m.TeamBilling.PaymentInformation)}
          text={
            <>
              {paymentSource && paymentSource.type === ChargebeePaymentType.Card && (
                <div>
                  <div>
                    <span style={{ textTransform: "capitalize" }}>{paymentSource?.cardBrand}</span>{" "}
                    ending {paymentSource?.lastFourDigits}
                  </div>
                  <div>
                    Expires: {("0" + paymentSource?.expirationMonth).slice(-2)}/
                    {paymentSource?.expirationYear?.toString().slice(-2)}
                  </div>
                </div>
              )}
              {paymentSource && paymentSource.type === ChargebeePaymentType.ApplePay && (
                <div>Apple Pay</div>
              )}
              {paymentSource && !PaymentTypes.includes(paymentSource.type) && (
                <div>Type: {paymentSource.type}</div>
              )}
              {!paymentSource && <div>{t(m.TeamBilling.NoPaymentInfo)}</div>}
            </>
          }
          CTA={
            <Button
              variant="borderless"
              onClick={() => {
                tracker.trackButtonClick({
                  action: ElementClickAction.EDIT_PAYMENT_INFORMATION,
                });
                setIsUpdatePaymentModalOpen(true);
              }}
              disabled={!accountData?.accountTeamID || !paymentSource}
            >
              {t(m.TeamBilling.Edit)}
            </Button>
          }
        />
        <BillingDetailsUpdateModal
          teamID={teamID}
          open={isUpdateBillingDetailsModalOpen}
          onClose={() => setIsUpdateBillingDetailsModalOpen(false)}
        />
        <PaymentUpdateModal
          teamID={teamID}
          open={isUpdatePaymentModalOpen}
          onClose={() => setIsUpdatePaymentModalOpen(false)}
          tertiaryText={
            accountData?.subscriptionInGracePeriod?.gracePeriodExpirationDate !== undefined
              ? `After submitting new details, your subscription may take some time to be updated with the revised credit card information.`
              : undefined
          }
        />
      </PageContainer>
    </UnderMaintenanceOrChildren>
  );
};

export default TeamBillingPage;
